/**
 * Sync queue for offline-first rating sync.
 * Uses IndexedDB with localStorage and memory fallbacks.
 */

import { openDB, type IDBPDatabase } from 'idb';

export interface RatingSyncData {
  countryCode: string;
  rating: number;
  updatedAt: number;
}

export interface SyncQueueItem {
  id: string;
  type: 'rating';
  data: RatingSyncData;
  dedupKey: string;
  createdAt: number;
  retries: number;
}

type SyncQueueInput = Omit<SyncQueueItem, 'id' | 'dedupKey' | 'createdAt' | 'retries'>;

// Storage backend priority: IndexedDB → localStorage → memory
type StorageBackend = 'indexeddb' | 'localstorage' | 'memory';

let storageBackend: StorageBackend = 'indexeddb';
let dbPromise: Promise<IDBPDatabase> | null = null;
let memoryQueue: SyncQueueItem[] = [];

const LOCALSTORAGE_KEY = 'witw-sync-queue';
const DB_NAME = 'witw-sync';
const DB_VERSION = 1;

/**
 * Generate a UUID, with fallback for environments where crypto.randomUUID is unavailable.
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: generate a v4-like UUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create IndexedDB database.
 * Falls back to localStorage/memory if IndexedDB unavailable.
 */
async function getDb(): Promise<IDBPDatabase | null> {
  if (storageBackend === 'memory') return null;
  if (storageBackend === 'localstorage') return null;

  try {
    if (!dbPromise) {
      dbPromise = openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          const store = db.createObjectStore('queue', { keyPath: 'id' });
          store.createIndex('dedupKey', 'dedupKey', { unique: true });
        },
      });
    }
    return await dbPromise;
  } catch (e) {
    console.warn('IndexedDB unavailable, falling back to localStorage:', e);
    storageBackend = 'localstorage';
    dbPromise = null;
    return null;
  }
}

/**
 * Get dedup key for a sync item.
 * Rating items are keyed by country code.
 */
function getDedupKey(item: SyncQueueInput): string {
  return `rating:${item.data.countryCode}`;
}

/**
 * Get queue from localStorage.
 */
function getLocalStorageQueue(): SyncQueueItem[] {
  try {
    const data = localStorage.getItem(LOCALSTORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

/**
 * Save queue to localStorage.
 */
function setLocalStorageQueue(queue: SyncQueueItem[]): void {
  try {
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.warn('localStorage unavailable, using memory queue:', e);
    storageBackend = 'memory';
    memoryQueue = queue;
  }
}

/**
 * Add item to sync queue with deduplication.
 * If an item with the same dedupKey exists, it's replaced (latest wins).
 */
export async function addToSyncQueue(item: SyncQueueInput): Promise<void> {
  const dedupKey = getDedupKey(item);
  const newItem: SyncQueueItem = {
    ...item,
    id: generateUUID(),
    dedupKey,
    createdAt: Date.now(),
    retries: 0,
  };

  const db = await getDb();

  if (db) {
    // IndexedDB path
    const tx = db.transaction('queue', 'readwrite');
    const store = tx.objectStore('queue');

    // Try to find existing item with same dedupKey
    const existingKey = await store.index('dedupKey').getKey(dedupKey);
    if (existingKey) {
      await store.delete(existingKey);
    }

    await store.put(newItem);
    await tx.done;
  } else if (storageBackend === 'localstorage') {
    // localStorage fallback
    const queue = getLocalStorageQueue();
    const filtered = queue.filter(q => q.dedupKey !== dedupKey);
    filtered.push(newItem);
    setLocalStorageQueue(filtered);
  } else {
    // Memory fallback (lost on page close)
    memoryQueue = memoryQueue.filter(q => q.dedupKey !== dedupKey);
    memoryQueue.push(newItem);
  }
}

/**
 * Get all items in sync queue.
 */
export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  const db = await getDb();

  if (db) {
    return db.getAll('queue');
  } else if (storageBackend === 'localstorage') {
    return getLocalStorageQueue();
  } else {
    return [...memoryQueue];
  }
}

/**
 * Remove a single item from queue.
 */
export async function removeFromQueue(id: string): Promise<void> {
  const db = await getDb();

  if (db) {
    await db.delete('queue', id);
  } else if (storageBackend === 'localstorage') {
    const queue = getLocalStorageQueue();
    setLocalStorageQueue(queue.filter(q => q.id !== id));
  } else {
    memoryQueue = memoryQueue.filter(q => q.id !== id);
  }
}

/**
 * Remove multiple items from queue.
 */
export async function removeFromQueueBatch(ids: string[]): Promise<void> {
  const idSet = new Set(ids);
  const db = await getDb();

  if (db) {
    const tx = db.transaction('queue', 'readwrite');
    await Promise.all(ids.map(id => tx.store.delete(id)));
    await tx.done;
  } else if (storageBackend === 'localstorage') {
    const queue = getLocalStorageQueue();
    setLocalStorageQueue(queue.filter(q => !idSet.has(q.id)));
  } else {
    memoryQueue = memoryQueue.filter(q => !idSet.has(q.id));
  }
}

/**
 * Clear entire sync queue.
 */
export async function clearSyncQueue(): Promise<void> {
  const db = await getDb();

  if (db) {
    await db.clear('queue');
  } else if (storageBackend === 'localstorage') {
    localStorage.removeItem(LOCALSTORAGE_KEY);
  } else {
    memoryQueue = [];
  }
}

/**
 * Get number of items in queue.
 */
export async function getQueueLength(): Promise<number> {
  const db = await getDb();

  if (db) {
    return db.count('queue');
  } else if (storageBackend === 'localstorage') {
    return getLocalStorageQueue().length;
  } else {
    return memoryQueue.length;
  }
}

/**
 * Get current storage backend (for debugging).
 */
export function getStorageBackend(): StorageBackend {
  return storageBackend;
}
